/**
 * The tenant home: a desktop-first GRID of the newsletters our AI runs, plus a
 * button into the wizard to create another. Each card surfaces the newsletter's
 * name, address, cadence, confirmed-subscriber count, and next-issue date, with a
 * primary "Open studio" into the issue editor
 * (/app/newsletters/:id), an "Audience" link into its audience surface (subscribers +
 * sending), and an overflow menu that holds the (confirmed) delete. The dashboard itself takes no action on a
 * newsletter beyond claiming any pending pre-signup wizard draft on first load.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Cadence, Newsletter, NewsletterInput, Tenant } from '@nuff/types';
import { Badge, Button, Notification, Skeleton } from '@nuff/ui/react';

import { ApiError, createNewsletter, deleteNewsletter, getMe, type ApiDeps } from '../api';
import { useAuth } from '../auth/context';
import { draftToNewsletterInput } from '../wizard/draft';
import { loadCurrentDraft, clearDraft } from '../wizard/storage';

type LoadState = 'loading' | 'ready' | 'error';

/**
 * Turn any pending pre-signup wizard draft into a real newsletter, claiming its
 * subdomain atomically. If the address was taken in the meantime (409), fall
 * back to creating the newsletter without it so the user never loses their setup
 * and can pick a new address afterwards. Returns the created newsletter (if any)
 * and a user-facing notice to surface.
 */
async function claimPendingDraft(
  deps: ApiDeps,
): Promise<{ created: Newsletter | null; notice: string | null }> {
  const draft = loadCurrentDraft();
  const base = draft ? draftToNewsletterInput(draft) : null;
  if (!draft || !base) {
    return { created: null, notice: null };
  }
  // Auto-capture the owner's timezone from the browser at claim time; the wizard
  // never asks for it and it is editable later in the newsletter's settings.
  const timezone = browserTimezone();
  const input: NewsletterInput = { ...base, ...(timezone ? { timezone } : {}) };
  try {
    const created = await createNewsletter(deps, input);
    clearDraft(draft.draftId);
    return { created, notice: null };
  } catch (caught) {
    if (caught instanceof ApiError && caught.status === 409 && input.subdomain !== undefined) {
      // Recreate without the taken address, but keep the focus topics (so source
      // discovery still has something to search on) and the captured timezone.
      const created = await createNewsletter(deps, {
        name: input.name,
        ...(input.topics ? { topics: input.topics } : {}),
        ...(timezone ? { timezone } : {}),
      });
      clearDraft(draft.draftId);
      return {
        created,
        notice: `The address "${input.subdomain}.nuffsaid.cloud" was just taken, so we created "${input.name}" without it. Pick a new address from its settings.`,
      };
    }
    return {
      created: null,
      notice:
        'We saved your setup but could not finish creating your newsletter. Try creating it again.',
    };
  }
}

/** The browser's IANA timezone, or null when the environment cannot resolve one. */
function browserTimezone(): string | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone && zone.length > 0 ? zone : null;
  } catch {
    return null;
  }
}

/** Human labels for each cadence; a draft without one reads "Not set". */
const CADENCE_LABELS: Record<Cadence, string> = {
  weekly: 'Weekly',
  biweekly: 'Every two weeks',
  monthly: 'Monthly',
};

/** How often this newsletter goes out, as plain English. */
function cadenceLabel(cadence: Cadence | null): string {
  return cadence ? CADENCE_LABELS[cadence] : 'Not set';
}

/** The newsletter's public address, or an honest "no address yet" placeholder. */
function addressLabel(subdomain: string | null): string {
  return subdomain !== null ? `${subdomain}.nuffsaid.cloud` : 'No address yet';
}

/** The next-issue date as a short "Fri, Aug 8", or "Not scheduled" for a draft. */
function nextIssueLabel(nextIssueAt: string | undefined): string {
  if (nextIssueAt === undefined) return 'Not scheduled';
  const when = new Date(nextIssueAt);
  if (Number.isNaN(when.getTime())) return 'Not scheduled';
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(when);
}

/** The confirmed-subscriber count as a label; the enrichment defaults to 0. */
function subscribersLabel(count: number | undefined): string {
  return `${String(count ?? 0)} confirmed`;
}

/**
 * The overflow ("...") menu on a card. Holds the destructive actions (today just
 * Delete) so a mis-click can never delete a newsletter from the card face. It is a
 * real menu: `aria-haspopup`, arrowless but keyboard-dismissible (Escape), and it
 * closes on an outside click.
 */
function OverflowMenu({
  name,
  onDelete,
  busy,
}: {
  readonly name: string;
  readonly onDelete: () => void;
  readonly busy: boolean;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent): void => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="dashboard__menu" ref={root}>
      <button
        type="button"
        className="dashboard__overflow"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`More actions for ${name}`}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span aria-hidden="true">⋯</span>
      </button>
      {open && (
        <ul className="dashboard__menu-list" role="menu">
          <li role="none">
            <button
              type="button"
              role="menuitem"
              className="dashboard__menu-item dashboard__menu-item--danger"
              aria-label={`Delete ${name}`}
              disabled={busy}
              onClick={() => {
                setOpen(false);
                onDelete();
              }}
            >
              Delete
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}

/** One newsletter, rendered as a rich, scannable card. */
function NewsletterCard({
  newsletter,
  onDelete,
  deleting,
}: {
  readonly newsletter: Newsletter;
  readonly onDelete: (target: Newsletter) => void;
  readonly deleting: boolean;
}): React.JSX.Element {
  const base = `/app/newsletters/${newsletter.newsletterId}`;
  return (
    <article className="dashboard__card">
      <div className="dashboard__card-top">
        <div className="dashboard__card-head">
          <h3 className="dashboard__card-name">{newsletter.name}</h3>
          <p className="dashboard__card-addr">{addressLabel(newsletter.subdomain)}</p>
        </div>
        <Badge status={newsletter.status} />
      </div>

      <dl className="dashboard__card-grid">
        <div className="dashboard__kv">
          <dt className="dashboard__k">Cadence</dt>
          <dd className="dashboard__v">{cadenceLabel(newsletter.cadence)}</dd>
        </div>
        <div className="dashboard__kv">
          <dt className="dashboard__k">Subscribers</dt>
          <dd className="dashboard__v">{subscribersLabel(newsletter.confirmedSubscribers)}</dd>
        </div>
        <div className="dashboard__kv">
          <dt className="dashboard__k">Next issue</dt>
          <dd className="dashboard__v">{nextIssueLabel(newsletter.nextIssueAt)}</dd>
        </div>
      </dl>

      <div className="dashboard__card-foot">
        <Link className="nuff-btn nuff-btn--primary nuff-btn--sm" to={base}>
          Open studio
        </Link>
        <Link className="nuff-btn nuff-btn--secondary nuff-btn--sm" to={`${base}/audience`}>
          Audience
        </Link>
        <span className="dashboard__foot-spacer" />
        <OverflowMenu
          name={newsletter.name}
          busy={deleting}
          onDelete={() => onDelete(newsletter)}
        />
      </div>
    </article>
  );
}

export function Dashboard(): React.JSX.Element {
  const { getIdToken, signOut } = useAuth();
  const navigate = useNavigate();

  const deps = useMemo<ApiDeps>(
    () => ({
      getIdToken,
      onUnauthorized: () => {
        signOut();
        void navigate('/signin');
      },
    }),
    [getIdToken, signOut, navigate],
  );

  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [newsletters, setNewsletters] = useState<readonly Newsletter[]>([]);
  const [draftNotice, setDraftNotice] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Latch the claim to a single in-flight promise so a double-mount or a retry
  // of load() can never read the draft twice (before clearDraft runs) and
  // double-create the newsletter.
  const claimRef = useRef<ReturnType<typeof claimPendingDraft> | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoadState('loading');
    setLoadError(null);
    try {
      const me = await getMe(deps);
      // First authenticated load: claim any pending pre-signup wizard draft,
      // exactly once across every load()/mount.
      claimRef.current ??= claimPendingDraft(deps);
      const { created, notice } = await claimRef.current;
      setTenant(me.tenant);
      setNewsletters(created ? [created, ...me.newsletters] : me.newsletters);
      setDraftNotice(notice);
      setLoadState('ready');
    } catch (caught) {
      setLoadError(caught instanceof Error ? caught.message : 'Could not load your dashboard.');
      setLoadState('error');
    }
  }, [deps]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleDelete = useCallback(
    async (target: Newsletter): Promise<void> => {
      if (!window.confirm(`Delete "${target.name}"? This cannot be undone.`)) {
        return;
      }
      setDeletingId(target.newsletterId);
      setDeleteError(null);
      try {
        await deleteNewsletter(deps, target.newsletterId);
        setNewsletters((current) => current.filter((n) => n.newsletterId !== target.newsletterId));
      } catch (caught) {
        setDeleteError(
          caught instanceof Error ? caught.message : 'Could not delete this newsletter.',
        );
      } finally {
        setDeletingId(null);
      }
    },
    [deps],
  );

  const totalConfirmed = useMemo(
    () => newsletters.reduce((sum, n) => sum + (n.confirmedSubscribers ?? 0), 0),
    [newsletters],
  );

  if (loadState === 'loading') {
    return (
      <section className="dashboard" aria-labelledby="dash-title">
        <header className="dashboard__head">
          <div>
            <h1 id="dash-title" className="dashboard__title">
              Your newsletters
            </h1>
            <p className="dashboard__sub" role="status">
              Loading everything our AI runs for you…
            </p>
          </div>
        </header>
        <div className="dashboard__grid" aria-hidden="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="dashboard__card dashboard__card--skeleton">
              <Skeleton lines={2} aria-label="Loading newsletter" />
              <Skeleton lines={4} />
              <Skeleton lines={1} />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (loadState === 'error') {
    return (
      <section className="dashboard" aria-labelledby="dash-title">
        <header className="dashboard__head">
          <h1 id="dash-title" className="dashboard__title">
            Your newsletters
          </h1>
        </header>
        <Notification variant="error">{loadError}</Notification>
        <Button className="app__btn-start" onClick={() => void load()}>
          Try again
        </Button>
      </section>
    );
  }

  return (
    <section className="dashboard" aria-labelledby="dash-title">
      <header className="dashboard__head">
        <div>
          {tenant !== null && (
            <p className="dashboard__eyebrow dashboard__plan">
              Plan: <strong>{tenant.plan}</strong> <Badge status={tenant.status} />
            </p>
          )}
          <h1 id="dash-title" className="dashboard__title">
            Your newsletters
          </h1>
          <p className="dashboard__sub">
            Everything our AI runs for you. Open one to work on its next issue, or manage how it
            sends.
          </p>
        </div>
        <Link className="nuff-btn nuff-btn--primary dashboard__new" to="/">
          New newsletter
        </Link>
      </header>

      {draftNotice !== null && <Notification variant="info">{draftNotice}</Notification>}
      {deleteError !== null && <Notification variant="error">{deleteError}</Notification>}

      {newsletters.length === 0 ? (
        <div className="dashboard__empty">
          <h2 className="dashboard__empty-title">No newsletters yet</h2>
          <p className="dashboard__empty-body">
            Create your first newsletter and our AI takes it from there, drafting issues from the
            sources you care about.
          </p>
          <Link className="nuff-btn nuff-btn--primary" to="/">
            Create your first newsletter
          </Link>
        </div>
      ) : (
        <>
          <div className="dashboard__metrics">
            <div className="dashboard__metric">
              <span className="dashboard__metric-n">{newsletters.length}</span>
              <span className="dashboard__metric-l">
                {newsletters.length === 1 ? 'Newsletter' : 'Newsletters'}
              </span>
            </div>
            <div className="dashboard__metric">
              <span className="dashboard__metric-n">{totalConfirmed.toLocaleString()}</span>
              <span className="dashboard__metric-l">Confirmed subscribers</span>
            </div>
          </div>

          <ul className="dashboard__grid">
            {newsletters.map((newsletter) => (
              <li key={newsletter.newsletterId} className="dashboard__grid-item">
                <NewsletterCard
                  newsletter={newsletter}
                  deleting={deletingId === newsletter.newsletterId}
                  onDelete={(target) => void handleDelete(target)}
                />
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
